/**
 * AppSidebar — Three-tier sidebar for the App Shell layout.
 *
 * Three modes: full (200px) | icon (48px) | thin (32px)
 * Owns the authenticated application's sidebar navigation.
 *
 * Full mode:  Nav links with labels, document sections, user menu, subscription banner
 * Icon mode:  Nav icons with tooltips, section icons with tooltips, collapse toggle
 * Thin mode:  Legacy compact gutter state; mobile hides the sidebar entirely
 *
 * See: docs/features/infrastructure/app-shell.md#sidebar-three-tier-collapse
 */
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    CalendarCheck,
    ContactRound,
    FileText,
} from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import { useAuth } from "../../utils/authContext";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { useDocumentSections } from "../../contexts/DocumentSectionsContext";
import { useAppShellContext } from "../../contexts/AppShellContext";
import SubscriptionBanner from "../../utils/SubscriptionBanner";
import InviteModal from "../../utils/InviteModal";
import { getUserInitials, getUserMenuOptions, handleUserMenuAction, performLogout } from "./UserMenu";

// ─── Component ──────────────────────────────────────────────────────────────

const AppSidebar = ({
    userData,
    onManageBilling,
}) => {
    const { currentUser, signOut } = useAuth();
    const { flags } = useFeatureFlags();
    const navigate = useNavigate();
    const location = useLocation();
    const { sections: documentSections, handleSectionClick } = useDocumentSections();
    const { sidebarMode, userMenuOpen, setUserMenuOpen } = useAppShellContext();

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const isActive = (path) => location.pathname === path;

    const isFull = sidebarMode === "full";
    const isIcon = sidebarMode === "icon";
    const isThin = sidebarMode === "thin";

    const handleLogout = () => performLogout(signOut, navigate);

    const handleMenuAction = (action) => {
        handleUserMenuAction(action, setUserMenuOpen, handleLogout, navigate, userData, setIsInviteModalOpen);
    };

    // Sidebar sections visible when viewing a document
    const sidebarSections = documentSections?.filter((s) => s.sidebar === true) || [];

    // Build class name
    let sidebarClass = "app-shell-sidebar";
    sidebarClass += ` mode-${sidebarMode}`;
    if (userMenuOpen) sidebarClass += " user-menu-open";

    return (
        <>
            <nav className={sidebarClass}>
                {/* ═══ FULL MODE ═══ */}
                {isFull && (
                    <>
                        <ul className="app-shell-sidebar-nav">
                            <li className={isActive("/dashboard") ? "active" : ""}>
                                <Link
                                    to="/dashboard"
                                >
                                    <Tooltip title="Today's Patients" arrow>
                                        <CalendarCheck className="app-shell-menu-icon" size={14} />
                                    </Tooltip>
                                    Today
                                </Link>
                            </li>
                            <li className={isActive("/patients") ? "active" : ""}>
                                <Link
                                    to="/patients"
                                >
                                    <Tooltip title="Your Patients" arrow>
                                        <ContactRound className="app-shell-menu-icon" size={14} />
                                    </Tooltip>
                                    Patients
                                </Link>
                            </li>
                        </ul>

                        {/* Document sections */}
                        {sidebarSections.length > 0 && (
                            <div className="app-shell-sections-container">
                                <hr className="app-shell-sidebar-divider" />
                                <ul className="app-shell-sections-list">
                                    {sidebarSections.map((section, idx) => (
                                        <li
                                            key={idx}
                                            className="app-shell-section-item"
                                            onClick={() => handleSectionClick(section)}
                                        >
                                            {section.title}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Bottom: subscription banner + user menu */}
                        <div className="app-shell-sidebar-bottom">
                            <SubscriptionBanner
                                userData={userData}
                                onManageBilling={onManageBilling}
                                variant="sidebar"
                            />

                            <div className="app-shell-user-info">
                                <span
                                    className="app-shell-user-trigger"
                                    onClick={() => setUserMenuOpen((o) => !o)}
                                    tabIndex={0}
                                    aria-haspopup="true"
                                    aria-expanded={userMenuOpen}
                                >
                                    <span className="app-shell-user-avatar">
                                        {getUserInitials(currentUser, "G", userData)}
                                    </span>
                                </span>
                                {userMenuOpen && (
                                    <div className="app-shell-user-menu">
                                        {getUserMenuOptions(userData?.userRole || "standard", userData, flags).map((option) => (
                                            <div
                                                key={option.action}
                                                className="app-shell-user-menu-item"
                                                onClick={() => handleMenuAction(option.action)}
                                            >
                                                {option.icon}
                                                {option.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* ═══ ICON MODE ═══ */}
                {isIcon && (
                    <>
                        <ul className="app-shell-sidebar-nav icon-nav">
                            <li className={isActive("/dashboard") ? "active" : ""}>
                                <Link
                                    to="/dashboard"
                                >
                                    <Tooltip title="Today" arrow placement="right">
                                        <CalendarCheck className="app-shell-menu-icon" size={18} />
                                    </Tooltip>
                                </Link>
                            </li>
                            <li className={isActive("/patients") ? "active" : ""}>
                                <Link
                                    to="/patients"
                                >
                                    <Tooltip title="Patients" arrow placement="right">
                                        <ContactRound className="app-shell-menu-icon" size={18} />
                                    </Tooltip>
                                </Link>
                            </li>
                        </ul>

                        {/* Document section icons */}
                        {sidebarSections.length > 0 && (
                            <div className="app-shell-sections-container icon-sections">
                                <hr className="app-shell-sidebar-divider" />
                                <ul className="app-shell-sections-list">
                                    {sidebarSections.map((section, idx) => (
                                        <li
                                            key={idx}
                                            className="app-shell-section-item icon-section-item"
                                            onClick={() => handleSectionClick(section)}
                                        >
                                            <Tooltip title={section.title} arrow placement="right">
                                                <FileText className="app-shell-section-icon" size={12} />
                                            </Tooltip>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* User avatar only (no email, no banner) */}
                        <div className="app-shell-sidebar-bottom">
                            <div className="app-shell-user-info">
                                <Tooltip title={currentUser?.email || "Account"} arrow placement="right">
                                    <span
                                        className="app-shell-user-trigger"
                                        onClick={() => setUserMenuOpen((o) => !o)}
                                        tabIndex={0}
                                        aria-haspopup="true"
                                        aria-expanded={userMenuOpen}
                                    >
                                        <span className="app-shell-user-avatar">
                                            {getUserInitials(currentUser, "G", userData)}
                                        </span>
                                    </span>
                                </Tooltip>
                                {userMenuOpen && (
                                    <div className="app-shell-user-menu">
                                        {getUserMenuOptions(userData?.userRole || "standard", userData, flags).map((option) => (
                                            <div
                                                key={option.action}
                                                className="app-shell-user-menu-item"
                                                onClick={() => handleMenuAction(option.action)}
                                            >
                                                {option.icon}
                                                {option.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* ═══ THIN MODE ═══ (legacy compact gutter state) */}
                {isThin && (
                    <div className="app-shell-sidebar-thin">
                        {/* Expand button handled by AppShell collapse toggle */}
                    </div>
                )}
            </nav>

            <InviteModal
                isOpen={isInviteModalOpen}
                setIsOpen={setIsInviteModalOpen}
                userData={userData}
            />
        </>
    );
};

export default AppSidebar;
